/**
 * 📸 VEHICLE IMAGE SERVICE
 * Handles vehicle image uploads to backend
 * Updated to use new /api/admin/vehicles/{vehicleId}/photos endpoints
 */

import { getApiBaseUrl } from '@/config/environment';


export interface VehicleImageData {
  url: string; // base64 or URL
  category: 'front' | 'back' | 'side' | 'interior' | 'dashboard' | 'other';
  isPrimary: boolean;
  displayOrder?: number;
}

export interface UploadVehicleImagesRequest {
  vehicleId: string;
  images: VehicleImageData[];
}

export interface UploadVehicleImagesResponse {
  success: boolean;
  data?: {
    vehicleId: string;
    uploadedImages: Array<{
      id: string;
      url: string;
      category: string;
      isPrimary: boolean;
    }>;
    totalImages: number;
  };
  message?: string;
  error?: string;
}

// Helper to get admin user ID from localStorage
const getAdminUserId = (): string => {
  // Try to get from user data in localStorage
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      return user.id || user.userId || 'admin-default';
    } catch (e) {
    }
  }
  return 'admin-default';
}

export const vehicleImageService = {
  /**
   * 📤 Upload vehicle images
   * Backend endpoint: POST /api/admin/vehicles/{vehicleId}/photos
   */
  async uploadVehicleImages(
    vehicleId: string,
    images: VehicleImageData[]
  ): Promise<UploadVehicleImagesResponse> {
    try {

      const adminUserId = getAdminUserId();

      const requestBody = {
        adminUserId: adminUserId,
        photos: images.map((img, index) => ({
          imageData: img.url, // base64 string with data:image/jpeg;base64, prefix
          categoryId: img.category,
          isPrimary: img.isPrimary,
          displayOrder: img.displayOrder ?? index
        }))
      };


      const response = await fetch(`${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Upload failed ${response.status}:`, errorText);

        let errorMessage = `Upload failed (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error uploading vehicle images:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload images'
      };
    }
  },

  /**
   * 📥 Get vehicle images
   * Backend endpoint: GET /api/admin/vehicles/{vehicleId}/photos
   */
  async getVehicleImages(vehicleId: string): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      url: string;
      category: string;
      isPrimary: boolean;
      createdAt: string;
    }>;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/photos`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Transform response to match expected format
      // Filter out proof of ownership documents (registration, proof_of_ownership, insurance)
      const proofOfOwnershipTypes = ['registration', 'proof_of_ownership', 'insurance'];
      const vehiclePhotos = (result.data?.photos || []).filter(
        (photo: any) => !proofOfOwnershipTypes.includes(photo.category)
      );

      return {
        success: true,
        data: vehiclePhotos
      };
    } catch (error) {
      console.error('❌ Error fetching vehicle images:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch images'
      };
    }
  },

  /**
   * 🗑️ Delete vehicle image
   * Backend endpoint: DELETE /api/admin/vehicles/{vehicleId}/photos/{photoId}
   * Note: Using admin endpoint instead of host endpoint for proper authentication
   */
  async deleteVehicleImage(vehicleId: string, photoId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      // Try admin endpoint first (consistent with GET and POST)
      const url = `${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/photos/${photoId}`;


      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete failed ${response.status}:`, errorText);

        let errorMessage = `Failed to delete (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
          console.error('❌ Backend Error Details:', errorJson);
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Photo deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting vehicle image:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete image'
      };
    }
  },

  /**
   * ⭐ Update primary image
   * Backend endpoint: PUT /api/admin/vehicles/{vehicleId}/photos/{photoId}/set-primary
   */
  async setPrimaryImage(vehicleId: string, photoId: string): Promise<{
    success: boolean;
    message?: string;
    data?: {
      vehicleId: string;
      photoId: string;
      isPrimary: boolean;
      previousPrimaryPhotoIds?: string[];
    };
    error?: string;
  }> {
    try {

      const url = `${getApiBaseUrl()}/api/admin/vehicles/${vehicleId}/photos/${photoId}/set-primary`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Set primary failed ${response.status}:`, errorText);

        let errorMessage = `Failed to set primary image (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Cover image updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error setting primary image:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to set primary image'
      };
    }
  }
};

export default vehicleImageService;
